import * as R from 'ramda'
import { useMemo } from 'react'
import { Room, RoomTypes } from '@weblite/ts-action/room'
import { Draft } from '@weblite/ts-action/draft'
import { Subscription } from '@weblite/ts-action/subscription'
import { useRooms, useRoomSyncStatus } from '$logic/room/react-hook'
import { useDrafts } from '$logic/draft/react-hook'
import { usePinnedRoomIdsAndWorkspaces } from '$logic/user-preferences/react-hook'
import { usePendingMessages } from '$logic/pending-messages/react-hook'
import { usePendingUploads } from '$logic/pending-uploads/react-hook'
import { PendingMessage } from '$logic/pending-messages/types'
import { PendingUpload } from '$logic/pending-uploads/types'
import { useRoomsLastMessage } from '$logic/room-last-message/react-hook'
import { getRoomLastMessageSync } from '$logic/room-last-message'
import { getPrivateSpace } from '$logic/private-space'
import { useCachedUserPermissions } from '$logic/user-permissions'
import { useSubscriptions } from '$logic/subscription/react-hook'
import { temporaryLastMessageStorage } from '$logic/room-last-message/storage'
import { usePinnedWorkspaceIds, useWorkspaces } from '$logic/workspace'
import { Workspace } from '@weblite/ts-action/workspace'

export const WorkspaceType = 'workspace' as const
export const WORKSPACES = 'WORKSPACES'

export interface RoomListItemBase {
  type: RoomTypes | typeof WorkspaceType
  chatId: string // single room
  pinned: boolean
  isOnTop?: boolean
}

export interface ChatRoomListItem extends RoomListItemBase {
  type: RoomTypes
  workspaceId?: string
}

export interface WorkspaceRoomListItem extends RoomListItemBase {
  type: typeof WorkspaceType
  workspaceChatIds: ChatRoomListItem[] // workspce room items
}

export type RoomListItem = ChatRoomListItem | WorkspaceRoomListItem

export type WorkspacesChatIdsType = Record<string, ChatRoomListItem[]>

function calculateListItems(
  pinnedRoomIdsAndWorkspaces: string[],
  rooms: Record<string, Room>,
  pendingMessages: PendingMessage[],
  pendingUploads: PendingUpload[],
  drafts: Record<string, Draft>,
  subscriptions: Record<string, Subscription>,
  includeWorkspace: boolean,
  pinnedWorkspaceIds: string[],
  workspaces: Record<string, Workspace>,
) {
  const groupedPending = R.groupBy(R.prop('roomId'), [
    ...pendingMessages,
    ...pendingUploads,
  ])

  const getRoomLastMessageDate = (room: Room) => {
    return R.reduce(
      R.max,
      0,
    )([
      drafts[room.id]?.date,
      temporaryLastMessageStorage.get(room.id)?.lastMessage?.date,
      getRoomLastMessageSync(room.id)?.date,
      room.createdAt,
      ...R.map(R.prop('pendingDate'), groupedPending[room.id] || []),
      room.type === 'CHANNEL' ? subscriptions[room.id]?.createdAt : -1,
    ])
  }

  const pinnedRoomIds = R.without(
    pinnedWorkspaceIds,
    pinnedRoomIdsAndWorkspaces,
  )

  if (includeWorkspace) {
    const notPinnedRooms: Room[] = R.values(R.omit(pinnedRoomIds, rooms)).sort(
      (a, b) => +getRoomLastMessageDate(b) - +getRoomLastMessageDate(a),
    )

    const chatRoomListItems: RoomListItem[] = [
      ...pinnedRoomIds.map(id => ({
        pinned: true,
        chatId: id,
        type: rooms[id]?.type,
        workspaceId: rooms[id]?.workspaceId,
      })),
      ...notPinnedRooms.map(room => ({
        pinned: false,
        chatId: room.id,
        type: room.type,
        workspaceId: room?.workspaceId,
      })),
    ]
    return {
      chatRoomListItems,
      workspaceRoomListItems: [],
      workspacesChatIds: {},
    }
  }

  const flattenRooms = R.toPairs(rooms).map(([_, room]) => room)

  const workspacesChatItems: WorkspacesChatIdsType = flattenRooms.reduce(
    (acc, item) => {
      if (!item.workspaceId) return acc
      return {
        ...acc,
        [item.workspaceId]: [
          ...(acc[item.workspaceId] || []),
          {
            type: rooms[item.id].type,
            chatId: item.id, // single room,
            pinned: pinnedRoomIds.includes(item.id),
            workspaceId: item.workspaceId,
          } as ChatRoomListItem,
        ],
      }
    },
    {},
  )

  // pinned rooms with no workspace map same as before
  const pinnedRoomsWithNoWorkspaces = pinnedRoomIds.filter(
    id => !rooms[id]?.workspaceId,
  )
  const pinnedRoomsData: ChatRoomListItem[] = pinnedRoomsWithNoWorkspaces.map(
    id => ({
      pinned: true,
      chatId: id,
      type: rooms[id]?.type,
      workspaceId: rooms[id]?.workspaceId,
    }),
  )

  // pinned workspaces .map
  const pinnedWorkspacesData: WorkspaceRoomListItem[] = pinnedWorkspaceIds.map(
    workspaceId => ({
      type: 'workspace',
      pinned: true,
      chatId: workspaceId,
      workspaceChatIds:
        workspacesChatItems[workspaceId]?.sort(
          (a, b) =>
            +getRoomLastMessageDate(rooms[b.chatId]) -
            +getRoomLastMessageDate(rooms[a.chatId]),
        ) || [],
    }),
  )

  // const sortedPinChatListItems = pinnedRoomsData.sort((a, b) => {
  //   return (
  //     pinnedRoomIdsAndWorkspaces.findIndex(id => a.chatId === id) -
  //     pinnedRoomIdsAndWorkspaces.findIndex(id => b.chatId === id)
  //   )
  // }) as RoomListItem[]

  // non pinned rooms with no workspace map same as before
  const nonPinnedRooms: Room[] = R.values(R.omit(pinnedRoomIds, rooms))

  const nonPinnedRoomsWithNoWorkspacesData: ChatRoomListItem[] = nonPinnedRooms
    .filter(room => !rooms[room.id].workspaceId)
    .map(room => ({
      type: room.type,
      chatId: room.id,
      pinned: false,
      lastMessageDate: getRoomLastMessageDate(room),
    }))

  // non pinned workspace data
  const nonPinnedWorkspaces = R.values(R.omit(pinnedWorkspaceIds, workspaces))

  const nonPinnedWorkspacesWithChats = nonPinnedWorkspaces.map(
    ({ id: workspaceId, createdAt }) => ({
      type: 'workspace' as const,
      pinned: false,
      chatId: workspaceId,
      workspaceChatIds:
        workspacesChatItems[workspaceId]?.sort(
          (a, b) =>
            +getRoomLastMessageDate(rooms[b.chatId]) -
            +getRoomLastMessageDate(rooms[a.chatId]),
        ) || [],
      lastMessageDate: createdAt,
    }),
  )

  const nonPinnedWorkspacesData: WorkspaceRoomListItem[] =
    nonPinnedWorkspacesWithChats
      .map(workspace => ({
        ...workspace,
        lastMessageDate: Math.max(
          ...workspace.workspaceChatIds.map(
            chat => +getRoomLastMessageDate(rooms[chat.chatId]),
          ),
          new Date(workspaces[workspace.chatId]?.createdAt).getTime(),
        ),
      }))
      .sort((a, b) => +b.lastMessageDate - +a.lastMessageDate)

  const sortedNonPinChatListItems = [
    ...nonPinnedRoomsWithNoWorkspacesData,
  ].sort((a, b) => +b.lastMessageDate - +a.lastMessageDate) as RoomListItem[]

  return {
    chatRoomListItems: [...pinnedRoomsData, ...sortedNonPinChatListItems],
    workspaceRoomListItems: [
      ...pinnedWorkspacesData,
      ...nonPinnedWorkspacesData,
    ],
    workspacesChatIds: workspacesChatItems,
  }
}

export let lastRoomListItems: RoomListItem[] = []

export function useRoomListItems(includeWorkspace = false): {
  chatRoomListItems: ChatRoomListItem[]
  workspaceRoomListItems: WorkspaceRoomListItem[]
  workspacesChatIds: WorkspacesChatIdsType
  loading: boolean
} {
  const rooms = useRooms()
  const workspaces = useWorkspaces()

  const drafts = useDrafts()
  const pinnedRoomIdsAndWorkspaces = usePinnedRoomIdsAndWorkspaces()
  const pinnedWorkspaceIds = usePinnedWorkspaceIds()
  const pendingMessages = usePendingMessages()
  const pendingUploads = usePendingUploads()
  const lastMessages = useRoomsLastMessage()
  const roomSyncStatus = useRoomSyncStatus()
  const subscriptions = useSubscriptions()

  const { chatRoomListItems, workspaceRoomListItems, workspacesChatIds } =
    useMemo(
      () =>
        rooms
          ? calculateListItems(
              pinnedRoomIdsAndWorkspaces,
              rooms,
              pendingMessages,
              pendingUploads,
              drafts,
              subscriptions,
              !!includeWorkspace,
              pinnedWorkspaceIds,
              workspaces,
            )
          : {
              chatRoomListItems: [],
              workspaceRoomListItems: [],
              workspacesChatIds: {},
            },
      [
        pinnedRoomIdsAndWorkspaces,
        rooms,
        pendingMessages,
        pendingUploads,
        drafts,
        lastMessages,
        subscriptions,
        includeWorkspace,
        pinnedWorkspaceIds,
        workspaces,
      ],
    )

  lastRoomListItems = chatRoomListItems
  return {
    chatRoomListItems,
    workspaceRoomListItems,
    loading:
      !(chatRoomListItems.length || workspaceRoomListItems.length) &&
      !roomSyncStatus.atLeastOnce,
    workspacesChatIds,
  }
}

export function addPrivateSpaceToRoomsList(roomsList: RoomListItem[]) {
  if (!roomsList.length) return []

  const privateSpaceRoomId = getPrivateSpace()?.roomId // TODO: Do we have privateSpace at start ?
  const isPrivateSpacePinned = R.reduce(
    (acc, item) =>
      !item.pinned
        ? R.reduced(false)
        : item.chatId === privateSpaceRoomId
          ? R.reduced(true)
          : false,
    false,
    roomsList,
  )

  return privateSpaceRoomId
    ? R.prepend(
        {
          type: 'PRIVATE_SPACE',
          chatId: privateSpaceRoomId,
          pinned: isPrivateSpacePinned,
          isOnTop: true,
        },
        roomsList,
      )
    : roomsList
}

export function useRoomsWithSendMessagePermission() {
  const { chatRoomListItems: rooms, loading } = useRoomListItems(true)
  const permissions = useCachedUserPermissions()

  const roomsWithPrivateSpace = useMemo(
    () =>
      addPrivateSpaceToRoomsList(
        rooms.filter(room => room.type !== 'PRIVATE_SPACE'),
      ),
    [rooms],
  )

  const loadingPermissions = useMemo(
    () => !loading && !Object.keys(permissions).length,
    [permissions, loading],
  )

  const items = useMemo(
    () =>
      loading
        ? []
        : roomsWithPrivateSpace.filter(
            item => permissions?.[item.chatId]?.sendMessage,
          ),

    [roomsWithPrivateSpace, permissions, loading],
  )

  return { items, loadingItems: loading, loadingPermissions }
}
